import {action, computed, observable} from 'mobx';
import FormStore from "./FormStore";
import {CLAIM_TYPES} from "../constants/claimTypes";
import * as yup from "yup";
import {ValidationError} from "yup";
import {stores} from "./index";

const {LOSS, DAMAGE} = CLAIM_TYPES;
const SUCCESS = "SUCCESS";
const ERROR = "ERROR";

const DEFAULT_FORM_DATA = {
    agreeToTerms: false,
    claimDescription: null,
    isRepairable: "yes",
    damageDescription: null,
    lossDate: null,
    claimType: LOSS,
    payeeFullName: null,
    address:{
        street:null,
        street2:null,
        city:null,
        state:null,
        zipcode:null,
    },
    saveAddressForFutureClaims: false,
}

class InsuranceStore {

    constructor(api) {
        this.api = api;
        this.coverage = null;
        this.claimForm = new FormStore(this);
        this.claimForm.data = DEFAULT_FORM_DATA;
    }


    @observable claimForm;
    @observable coverage;
    @observable claimDescriptionOptions = [];
    @observable claimDocumentTypeOptions = [];
    @observable claimStatuses = [];
    @observable error;
    @observable loading;
    @observable submitting;
    @observable claim;
    @observable createClaimStep = 1;
    @observable isOneStepClaim = false;

    zipcodeRegExp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    claimSchema = yup.object({
        payeeFullName: yup
            .string()
            .trim()
            .required('Company Name is required'),
        address: yup.object({
            street: yup
                .string()
                .trim(),
            street2: yup
                .string()
                .trim(),
            city: yup
                .string()
                .trim(),
            state: yup
                .string()
                .trim(),
            zipcode: yup
                .string()
                .trim()
                .matches(this.zipcodeRegExp, { message: 'Invalid format.  Please use the format 00000 or 00000-0000', excludeEmptyString: true }),
        }).test('validAddress',
            'Address is invalid',
            function (item,testContext) {
                const keys = ['street', 'street2', 'city', 'state', 'zipcode']
                const mappedKeys = new Map();
                mappedKeys.set('street', 'Address Line 1');
                mappedKeys.set('street2', 'Address Line 2');
                mappedKeys.set('city', 'City');
                mappedKeys.set('state', 'State');
                mappedKeys.set('zipcode', 'Zipcode');

                const errors = keys.filter(k => k !== 'street2').map((key) => {
                        const value = item[key];
                        if (item[key] ) {
                            return null
                        } // Successful validation, no error

                        let mappedKey = mappedKeys.get(key);
                        if(mappedKey === undefined) {
                            mappedKey = key;
                        }
                        return new ValidationError(
                            `${mappedKey} is required`,
                            value,
                            `address.${key}`
                        )
                    }).filter(Boolean)

                if (errors.length === 0) { return true }

                return testContext.createError({
                    message: () => errors
                })
            })

    });

    @action
    getCoverage = (shipmentId) => {
        this.loading = true;
        this.coverage = null;
        this.error = null;
        this.claim = null;
        this.clearForm();
        if(shipmentId == null || shipmentId == ""){
            return;
        }
        const url = `/insurance/${shipmentId}/coverage`;
            this.api.get(url)
              .then(response => {
                  if (response.status == 200){
                      if(response.data.status === SUCCESS){

                          if(response.data?.isInsuranceClaimCreated){
                              this.error = "A claim has already been created for this shipment" + (response.data?.claimNumber ? ". Claim Number: " + response.data.claimNumber : "");
                          }else{
                              this.coverage = response.data;
                          }
                          if(!this.coverage?.clientPaymentDetailsRequired){
                              this.isOneStepClaim = true;
                          }

                      }else{
                          this.error = response.data.message;
                      }
                  }else{
                      this.error = response.data.message;
                  }
              }).catch((e) => {
                this.error = !!e ? e : 'An error occurred while retrieving the insurance information. Please try again.'
            }).finally(() => {
                this.loading = false;
            });
    }
    @action
    clearCoverage = () => {
        this.coverage = null;
        this.error = null;
        this.clearForm();
    }

    @action
    clearForm = () => {
        this.claimForm.data = DEFAULT_FORM_DATA;
        this.claimForm.validationErrors = {};
    }


    @action
     createClaim = async (onSuccess) => {
        const shipmentId = this.coverage.shipmentId;
        this.submitting = true;
        this.error = null;


        let claimRequest = { ...this.claimForm.data,
            isRepairable: !!this.claimForm.data.isRepairable && this.claimForm.data.isRepairable == 'yes' ? true : false,
            payeeFullName: this.claimForm.data.payeeFullName,
            payeeAddressLine1: this.claimForm.data.address.street,
            payeeAddressLine2: this.claimForm.data.address.street2,
            payeeCity: this.claimForm.data.address.city,
            payeeState: this.claimForm.data.address.state,
            payeeZipCode: this.claimForm.data.address.zipcode,

            contactAddressLine1: this.claimForm.data.address.street,
            contactAddressLine2: this.claimForm.data.address.street2,
            contactCity: this.claimForm.data.address.city,
            contactState: this.claimForm.data.address.state,
            contactZipCode: this.claimForm.data.address.zipcode,

            damageDescription: this.showDamaged ? this.claimForm.data.claimDescription : null,
            saveAddressForFutureClaims: this.claimForm.data.saveAddressForFutureClaims
        };

        if (!this.showDamaged) {
            delete claimRequest['isRepairable'];
            delete claimRequest['damageDescription'];
        }

        const url = `/insurance/${shipmentId}/claim`;
        await this.api.post(url, claimRequest)
            .then(response => {
                if (response.status == 200){
                    if(response.data.status === SUCCESS) {
                        this.claim = response.data;
                        onSuccess(response.data);
                    }else{
                        this.error = response.data.message;
                    }
                }else {
                    if (!!response.data.error) {
                        this.error = response.data.error;
                    } else if (!!response.data.errors && response.data.errors.length > 0) {
                        this.error = response.data.errors[0];
                    } else {
                        this.error = 'An error occurred while creating the claim. Please try again.';
                    }
                }
            }).catch((e) => {
                this.error = e;
        }).finally(() => {
            this.submitting = false;
        });

        if(!!this.claimForm.data.saveAddressForFutureClaims) {
            await stores.userStore.getUser()
        }
    }


    @action
    getClaimDescriptions = () => {
        this.loading = true;
        const url = `/insurance/claim-descriptions`;
        return this.api.get(url)
            .then(response => {
                if (response.status == 200){
                    const claimDescriptions = response.data;
                    const claimDescriptionOptions = [];
                    Object.keys(claimDescriptions).map((key, index) => {
                        const value = claimDescriptions[key];
                        claimDescriptionOptions.push({'label':key,'value':key, 'type':value});
                    });
                    this.claimDescriptionOptions = claimDescriptionOptions;
                }else{
                    this.error = response.data;
                }
            }).catch((e) => {
            this.error = e;
        }).finally(() => {
            this.loading = false;
        });
    }

    @action
    getClaimDocumentTypes = () => {
        this.loading = true;
        const url = `/insurance/claim-document-types`;
        return this.api.get(url)
            .then(response => {
                if (response.status == 200){
                    const claimDocumentTypes = response.data;
                    const claimDocumentTypeOptions = [];
                    Object.keys(claimDocumentTypes).map((key, index) => {
                        const value = claimDocumentTypes[key];
                        claimDocumentTypeOptions.push({'label':key,'value':value, 'type':value});
                    });
                    this.claimDocumentTypeOptions = claimDocumentTypeOptions;
                }else{
                    this.error = response.data;
                }
            }).catch((e) => {
                this.error = e;
            }).finally(() => {
                this.loading = false;
            });
    }

    @action reset =() =>{
        this.loading = false;
        this.submitting = false;
        this.coverage = null;
        this.error = null;
        this.createClaimStep = 1
        this.clearForm()
    }

    @action setCreateClaimStep = (step) =>{
        this.createClaimStep = step;
    }

    @action
    setError = (error) =>{
       this.error = error;
    }

    @computed get showDamaged(){
        if(this.claimForm.getField("claimDescription",null) !== null && this.claimForm.getField("claimDescription",null).toLowerCase().includes("damage")){
            return true;
        }
        return false;
    }

    @computed
    get canSubmit() {
        if(this.isOneStepClaim){
            const claimDescription = this.claimForm.getField("claimDescription", null);
            const lossDate = this.claimForm.getField("lossDate", null);
            if (this.coverage && this.claimForm.getField("agreeToTerms") === true && this.claimForm.getField("claimDescription") != null
                    && lossDate && claimDescription) {
                if (this.showDamaged && this.claimForm.getField("isRepairable", null) === null) {
                    return false;
                }
                return true;
            }
            return false;
        }else {
            if (this.createClaimStep === 1) {
                const claimDescription = this.claimForm.getField("claimDescription", null);
                const lossDate = this.claimForm.getField("lossDate", null);
                return this.coverage && claimDescription && lossDate;
            }

            if (this.coverage && this.claimForm.getField("agreeToTerms") === true && this.claimForm.getField("claimDescription") != null) {

                if (this.showDamaged && this.claimForm.getField("isRepairable", null) === null) {
                    return false;
                }
                return true;
            }
            return false;
        }
    }

    @action
    getClaims(limit,offset,shipmentSearchString){
        this.loading = true;
        const url = `/insurance/claims`;
        const params = {
            limit: limit,
            offset: offset,
            shipmentSearchString: shipmentSearchString
        };

        this.api.post(url,params)
            .then(response => {
                if (response.status == 200){
                    this.claims = response.data;
                }else{
                    this.error = response.data;
                }
            }).catch((e) => {
            this.error = e;
        }).finally(() => {
            this.loading = false;
        });
    }

    @action
    validateClaimForm() {
        return this.claimForm.isValid(this.claimSchema)
    }


    @action
    getClaimsStatuses = () => {
        this.loading = true;
        const url = `/insurance/claim-statuses`;
        return this.api.get(url)
            .then(response => {
                if (response.status == 200){
                    const claimProviders = response.data;
                    const claimStatusOptions = [];
                    Object.keys(claimProviders).map((provider, index) => {
                        const claimStatuses = claimProviders[provider];
                        Object.keys(claimStatuses).map((key, index) => {
                            const claimStatus = claimStatuses[key];
                            const label = claimStatus.label;
                            const description = claimStatus.description;
                            claimStatusOptions.push({label: label, value: key, type: label, provider: provider, description: description});
                        })
                    });

                    this.claimStatuses = claimStatusOptions.map(status => ({title: status.label, description: status?.description?.trim()}));
                }else{
                    this.error = response.data;
                }
            }).catch((e) => {
                this.error = e;
            }).finally(() => {
                this.loading = false;
            });
    }

}


export default InsuranceStore;